import { toast } from 'react-toastify';
import { useBoolean } from 'react-use';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { Form } from '@ui';
import { isNil } from 'lodash-es';
import { useContext, useMemo } from 'react';
import { AuthContext } from '@lp/core/auth';
import { getCaseValuesToPatch } from '@lp/features/forms/CreateCaseForm/utils';
import { caseSchema } from '@lp/features/forms/CreateCaseForm/config';
import { CreateCaseForm, FormActions } from '@lp/features/forms/CreateCaseForm';
import {
  buildUnassignTxUsersIfDA,
  getCaseFormDefaultValues,
  getTxAssigneesDiff,
  getTXCenterORUpdatedValues,
} from '@features/components/CaseFormBlocks/utils';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { useCanCreateKidneyCase } from '@lp/core/hooks/useCanCreateKidneyCase';
import {
  updateTxCenterOR,
  useMoveCaseToNextStatusMutation,
  useUpdateCaseMutation,
  useUpdateTxCenterORMutation,
} from '@/queries/case';
import { useUpdateCaseFiles } from '@/hooks/useUpdateCaseFiles';
import { getOPOFormContacts, mapAssignedOPOContacts, useUpdateOpoContacts } from '@/hooks/useUpdateOpoContacts';
import { useUpdateLinks } from '@/hooks/useUpdateLinks';
import { retrieveUnifiedError } from '@/api/utils';
import { Case, CaseStatus, ClinicalItemType, DraftCase, TxCenterORInitialData } from '@/api/apis/case';
import { CaseBoardPermissions } from '@/permissions';
import { useReallocationFilesTransfer } from '@/queries/file';
import { useSetNrp } from '@/hooks/useSetNrp';
import { getClinicalRecordByType } from '@/utils/helpers';

type EditDraftCaseModalProps = {
  onClose: () => void;
  caseInfo: Case | DraftCase;
};

export const EditDraftCaseModal = ({ onClose, caseInfo }: EditDraftCaseModalProps) => {
  const { mutateAsync: updateCase } = useUpdateCaseMutation();
  const { mutateAsync: updateTxCenterORMutation } = useUpdateTxCenterORMutation();
  const { updateNrp } = useSetNrp();
  const updateCaseFiles = useUpdateCaseFiles();
  const updateOpoContacts = useUpdateOpoContacts();
  const updateLinks = useUpdateLinks();
  const { mutateAsync: transferFiles } = useReallocationFilesTransfer();
  const { mutateAsync: moveToScreening } = useMoveCaseToNextStatusMutation();
  const [isSubmitting, setIsSubmitting] = useBoolean(false);

  const { checkPermission } = useContext(AuthContext);
  const canChangeDraftCases = checkPermission(CaseBoardPermissions.CaseDraftUpdate);
  const canCreateKidneyCase = useCanCreateKidneyCase();

  const defaultFormValues = useMemo(() => getCaseFormDefaultValues(caseInfo), [caseInfo]);

  const prepareCaseData = async (formData: CaseFormType) => {
    const { files, links, donorHospitalTimeZone, donorHospitalDate, donorHospitalTime, serviceRegionId } = formData;

    const { files: prevFiles, links: prevLinks, assignedOpoContacts, caseId } = caseInfo;
    const prevFormValues = getCaseFormDefaultValues(caseInfo);
    const {
      nrp,
      nrpType,
      opoContactId,
      secondaryOpoContactId,
      opoSourceContactPhone,
      secondaryOpoContactPhone,
      txCenterCustomLocation,
      coordinatorId,
      receivingSurgeonId,
      originalReAllocationCaseId,
      originalOpoRequest,
      recipientHospitalDate,
      recipientHospitalTime,
      recipientHospitalTimeZone,
      ...valuesToPatch
    } = getCaseValuesToPatch(prevFormValues, formData);

    const defaultTxAssignees = getTxAssigneesDiff(prevFormValues, formData);

    const forcedUnassign = buildUnassignTxUsersIfDA(
      caseInfo.serviceType,
      (formData as CaseFormType).serviceType ?? caseInfo.serviceType,
      caseInfo.assignedCustomers,
    );

    const assignees = [...forcedUnassign];

    const shouldSendUpdate =
      Object.values(valuesToPatch).length > 0 || defaultTxAssignees.length > 0 || assignees.length > 0;

    let caseUpdated = shouldSendUpdate
      ? await updateCase({
          ...valuesToPatch,
          donorHospitalDate,
          donorHospitalTime,
          donorHospitalTimeZone,
          caseId,
          txCenterCustomLocation: valuesToPatch.txCenterId ? txCenterCustomLocation : undefined,
          ...(defaultTxAssignees.length ? { defaultTxAssignees } : {}),
          ...(assignees.length ? { assignees } : {}),
          serviceRegionId,
        })
      : caseInfo;

    const txCenterORValues = getTXCenterORUpdatedValues(
      { recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone },
      defaultFormValues,
    );

    if (txCenterORValues) {
      caseUpdated = await updateTxCenterOR({
        updateMutation: updateTxCenterORMutation,
        caseId,
        data: txCenterORValues as TxCenterORInitialData,
      });
    }

    await updateLinks(prevLinks ?? [], links, caseId);

    const nrpData = getClinicalRecordByType(caseInfo.clinicalData, ClinicalItemType.NRPApplicabilityAndType);
    await updateNrp(formData.nrp, formData.nrpType, !isNil(nrp), !isNil(nrpType), caseId, nrpData?.item.id);

    if (valuesToPatch.reAllocationCaseId) {
      await transferFiles({ caseId });
    }

    const existingContactIds = mapAssignedOPOContacts(assignedOpoContacts);
    const newOpoContacts = getOPOFormContacts(
      formData.opoContactId,
      formData.secondaryOpoContactId,
      formData.opoSourceContactPhone,
      formData.secondaryOpoContactPhone,
    );
    await updateOpoContacts(existingContactIds, newOpoContacts, caseId);
    await updateCaseFiles(prevFiles, files, caseId);

    return caseUpdated;
  };

  const handleSubmit = async (data: CaseFormType, proceedToScreening: boolean) => {
    try {
      setIsSubmitting(true);
      const caseResponse = await prepareCaseData(data);

      if (caseResponse && proceedToScreening) {
        await moveToScreening({
          caseId: caseResponse.caseId,
          nextStatus: CaseStatus.ClinicalScreening,
        });
      }

      toast.success(proceedToScreening ? 'Case created successfully.' : 'Draft Case updated successfully.');
      onClose();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScreeningSubmit = (data: CaseFormType) => handleSubmit(data, true);
  const handleDraftSubmit = (data: CaseFormType) => handleSubmit(data, false);

  return (
    <Modal
      classNames={{
        wrapper: 'flex-wrap',
        base: 'p-6 bg-base-80 !m-2 max-h-[95vh]',
        header: '!text-hl font-secondary p-0 pb-4 border-b-1 border-base-30',
        body: 'p-0 my-6',
        footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
      }}
      size="5xl"
      backdrop="opaque"
      isOpen
      onClose={onClose}
      scrollBehavior="inside"
      hideCloseButton
    >
      <Form<CaseFormType> defaultValues={defaultFormValues} schema={caseSchema} onSubmit={handleScreeningSubmit}>
        <ModalContent className="scroll">
          {(handleClose) => (
            <>
              <ModalHeader>Edit Case</ModalHeader>
              <ModalBody>
                <CreateCaseForm
                  hasPermission={canChangeDraftCases}
                  reAllocationCaseId={caseInfo.reAllocationCaseId}
                  caseId={caseInfo.caseId}
                  caseStatus={caseInfo.status}
                  isEditDraft
                  canCreateKidneyCase={canCreateKidneyCase}
                  originalOrganType={caseInfo.organType}
                />
              </ModalBody>
              <ModalFooter>
                <FormActions
                  handleClose={handleClose}
                  isLoading={isSubmitting}
                  handleSubmitDraft={handleDraftSubmit}
                  canCreateKidneyCase={canCreateKidneyCase}
                  isEditMode
                  originalOrganType={caseInfo.organType}
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Form>
    </Modal>
  );
};
